import {
    Alert,
    Autocomplete,
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Chip,
    CircularProgress,
    Collapse,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    FormControl,
    IconButton,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Stack,
    TextField,
    Tooltip,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";

import {
    AddAlertOutlined as AddAlertOutlinedIcon,
    Close as CloseIcon,
    DeleteOutline as DeleteOutlineIcon,
} from "@mui/icons-material";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExcel } from "@fortawesome/free-solid-svg-icons";

import { tokens } from "../../theme";
import { djangoApi } from "../../utils/axios/axios.utils";

import ProductAccordion from "../../components/open-prods";
import { exportToExcelOpenProds } from "../../components/open-prods/excel-handler";

import {
    selectIsAdminUser,
    selectIsOfficeStaffUser,
    selectIsStaffUser,
} from "../../store/user/user.selector";


const FILTER_OPTIONS = ["Vendedoras", "Estoque"];

const ALERT_UI = {
    background: "#F7F8F6",
    surface: "#FFFFFF",
    surfaceSoft: "#FBFCFA",
    text: "#253129",
    textSecondary: "#6F7B73",
    border: "#DDE5DF",
    primary: "#3E7C59",
    primaryDark: "#315F45",
    primarySoft: "#EFF7F1",
    primaryBorder: "#BFD8C7",
    danger: "#B84C55",
    dangerSoft: "#FFF4F4",
};

const LIGHT_MODAL_FIELD_SX = {
    "& .MuiInputLabel-root": {
        color: ALERT_UI.textSecondary,
    },
    "& .MuiInputLabel-root.Mui-focused": {
        color: ALERT_UI.primary,
    },
    "& .MuiOutlinedInput-root": {
        color: ALERT_UI.text,
        backgroundColor: "#FFFFFF",
        borderRadius: 2,
        "& fieldset": {
            borderColor: ALERT_UI.border,
        },
        "&:hover fieldset": {
            borderColor: ALERT_UI.primaryBorder,
        },
        "&.Mui-focused fieldset": {
            borderColor: ALERT_UI.primary,
            borderWidth: 1,
        },
    },
    "& .MuiFormHelperText-root": {
        color: ALERT_UI.textSecondary,
    },
};

const ENDPOINTS = {
    openProducts: "products/register_prods/get_all_kitprods_open/",
    stockProducts: "products/register_prods/get_prods_ajustes_preco/",
    searchAlertProducts: "products/register_prods/search_products_for_alert/",
    alertSellers: "products/register_prods/get_sellers_for_product_alert/",
    createAlert: "products/register_prods/create_product_return_alert/",
    listAlerts: "products/register_prods/get_product_return_alerts/",
    cancelAlert: "products/register_prods/cancel_product_return_alert/",
};


const getAuthHeaders = () => ({
    Authorization: `Token ${process.env.REACT_APP_DJANGO_TOKEN}`,
    "Content-Type": "application/json",
});


const getProductOptionLabel = (option) => {
    if (!option) return "";

    const parts = [
        option.product_code,
        option.product_model_label || option.product_model,
        option.product_size ? `Tam. ${option.product_size}` : null,
        option.color || null,
    ].filter(Boolean);

    return parts.join(" • ");
};


const formatDateTimeBR = (value) => {
    if (!value) return "-";

    try {
        return new Intl.DateTimeFormat("pt-BR", {
            dateStyle: "short",
            timeStyle: "short",
        }).format(new Date(value));
    } catch {
        return value;
    }
};




const LIGHT_AUTOCOMPLETE_COMPONENTS_PROPS = {
    paper: {
        sx: {
            mt: 0.75,
            backgroundColor: "#FFFFFF !important",
            backgroundImage: "none !important",
            color: `${ALERT_UI.text} !important`,
            border: `1px solid ${ALERT_UI.border}`,
            borderRadius: 2,
            boxShadow: "0 14px 34px rgba(15, 23, 42, 0.14)",
            overflow: "hidden",
            "& .MuiAutocomplete-listbox": {
                backgroundColor: "#FFFFFF !important",
                color: `${ALERT_UI.text} !important`,
                padding: "6px",
            },
            "& .MuiAutocomplete-option": {
                backgroundColor: "#FFFFFF !important",
                color: `${ALERT_UI.text} !important`,
                "& *": {
                    color: "inherit",
                },
                borderRadius: 1.5,
                marginBottom: "2px",
                "&:hover": {
                    backgroundColor: "#F4F8F5 !important",
                },
                "&.Mui-focused": {
                    backgroundColor: "#F4F8F5 !important",
                },
                "&[aria-selected='true']": {
                    backgroundColor: `${ALERT_UI.primarySoft} !important`,
                },
                "&[aria-selected='true'].Mui-focused": {
                    backgroundColor: "#DDF2E6 !important",
                },
            },
            "& .MuiAutocomplete-noOptions": {
                backgroundColor: "#FFFFFF !important",
                color: `${ALERT_UI.textSecondary} !important`,
            },
            "& .MuiAutocomplete-loading": {
                backgroundColor: "#FFFFFF !important",
                color: `${ALERT_UI.textSecondary} !important`,
            },
        },
    },
};

const ProductReturnAlertModal = ({
    open,
    onClose,
    colors,
}) => {
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));

    const [productSearch, setProductSearch] = useState("");
    const [productOptions, setProductOptions] = useState([]);
    const [selectedProduct, setSelectedProduct] = useState(null);
    const [isSearchingProducts, setIsSearchingProducts] = useState(false);

    const [sellerOptions, setSellerOptions] = useState([]);
    const [selectedSeller, setSelectedSeller] = useState(null);
    const [isLoadingSellers, setIsLoadingSellers] = useState(false);

    const [message, setMessage] = useState("");

    const [activeAlerts, setActiveAlerts] = useState([]);
    const [isLoadingAlerts, setIsLoadingAlerts] = useState(false);

    const [isSaving, setIsSaving] = useState(false);
    const [cancelingAlertId, setCancelingAlertId] = useState(null);

    const [feedback, setFeedback] = useState({
        type: "",
        message: "",
    });

    const clearFeedback = () => {
        setFeedback({
            type: "",
            message: "",
        });
    };

    const loadSellers = useCallback(async () => {
        setIsLoadingSellers(true);

        try {
            const response = await djangoApi.get(
                ENDPOINTS.alertSellers,
                {
                    headers: getAuthHeaders(),
                }
            );

            setSellerOptions(response.data?.data || []);
        } catch (error) {
            console.error("Erro ao carregar vendedoras:", error);

            setFeedback({
                type: "error",
                message:
                    error.response?.data?.msg ||
                    "Não foi possível carregar as vendedoras.",
            });
        } finally {
            setIsLoadingSellers(false);
        }
    }, []);

    const loadActiveAlerts = useCallback(async () => {
        setIsLoadingAlerts(true);

        try {
            const response = await djangoApi.get(
                ENDPOINTS.listAlerts,
                {
                    params: {
                        status: "ACTIVE",
                    },
                    headers: getAuthHeaders(),
                }
            );

            setActiveAlerts(response.data?.data || []);
        } catch (error) {
            console.error("Erro ao carregar alertas:", error);

            setFeedback({
                type: "error",
                message:
                    error.response?.data?.msg ||
                    "Não foi possível carregar os alertas ativos.",
            });
        } finally {
            setIsLoadingAlerts(false);
        }
    }, []);

    useEffect(() => {
        if (!open) return;

        clearFeedback();
        loadSellers();
        loadActiveAlerts();
    }, [open, loadSellers, loadActiveAlerts]);

    useEffect(() => {
        if (!open) return;

        const normalizedSearch = productSearch.trim();

        if (normalizedSearch.length < 2) {
            setProductOptions([]);
            setIsSearchingProducts(false);
            return;
        }

        let requestIsActive = true;

        const timeoutId = window.setTimeout(async () => {
            setIsSearchingProducts(true);

            try {
                const response = await djangoApi.get(
                    ENDPOINTS.searchAlertProducts,
                    {
                        params: {
                            search: normalizedSearch,
                        },
                        headers: getAuthHeaders(),
                    }
                );

                if (requestIsActive) {
                    setProductOptions(response.data?.data || []);
                }
            } catch (error) {
                console.error(
                    "Erro ao pesquisar produtos para alerta:",
                    error
                );

                if (requestIsActive) {
                    setProductOptions([]);

                    setFeedback({
                        type: "error",
                        message:
                            error.response?.data?.msg ||
                            "Não foi possível pesquisar os produtos.",
                    });
                }
            } finally {
                if (requestIsActive) {
                    setIsSearchingProducts(false);
                }
            }
        }, 450);

        return () => {
            requestIsActive = false;
            window.clearTimeout(timeoutId);
        };
    }, [open, productSearch]);

    const resetForm = () => {
        setProductSearch("");
        setProductOptions([]);
        setSelectedProduct(null);
        setSelectedSeller(null);
        setMessage("");
        clearFeedback();
    };

    const handleClose = () => {
        if (isSaving) return;

        resetForm();
        onClose();
    };

    const handleCreateAlert = async () => {
        clearFeedback();

        if (!selectedProduct) {
            setFeedback({
                type: "error",
                message: "Selecione o produto que deve gerar o alerta.",
            });
            return;
        }

        if (!message.trim()) {
            setFeedback({
                type: "error",
                message: "Informe a descrição do alerta.",
            });
            return;
        }

        setIsSaving(true);

        try {
            const payload = {
                content_type_id: selectedProduct.content_type_id,
                object_id: selectedProduct.object_id,
                requested_by_seller_id: selectedSeller?.id || null,
                message: message.trim(),
            };

            const response = await djangoApi.post(
                ENDPOINTS.createAlert,
                payload,
                {
                    headers: getAuthHeaders(),
                }
            );

            setFeedback({
                type: "success",
                message:
                    response.data?.msg ||
                    "Alerta criado com sucesso.",
            });

            setProductSearch("");
            setProductOptions([]);
            setSelectedProduct(null);
            setSelectedSeller(null);
            setMessage("");

            await loadActiveAlerts();
        } catch (error) {
            console.error("Erro ao criar alerta:", error);

            setFeedback({
                type: "error",
                message:
                    error.response?.data?.msg ||
                    "Não foi possível criar o alerta.",
            });
        } finally {
            setIsSaving(false);
        }
    };

    const handleCancelAlert = async (alertId) => {
        const confirmed = window.confirm(
            "Deseja cancelar este alerta?"
        );

        if (!confirmed) return;

        clearFeedback();
        setCancelingAlertId(alertId);

        try {
            const response = await djangoApi.post(
                ENDPOINTS.cancelAlert,
                {
                    alert_id: alertId,
                },
                {
                    headers: getAuthHeaders(),
                }
            );

            setFeedback({
                type: "success",
                message:
                    response.data?.msg ||
                    "Alerta cancelado com sucesso.",
            });

            await loadActiveAlerts();
        } catch (error) {
            console.error("Erro ao cancelar alerta:", error);

            setFeedback({
                type: "error",
                message:
                    error.response?.data?.msg ||
                    "Não foi possível cancelar o alerta.",
            });
        } finally {
            setCancelingAlertId(null);
        }
    };

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullScreen={fullScreen}
            fullWidth
            maxWidth="md"
            PaperProps={{
                sx: {
                    borderRadius: fullScreen ? 0 : 3.5,
                    backgroundImage: "none !important",
                    backgroundColor: `${ALERT_UI.background} !important`,
                    color: `${ALERT_UI.text} !important`,
                    boxShadow: "0 24px 70px rgba(34, 48, 40, 0.18)",
                    border: `1px solid ${ALERT_UI.border}`,
                    overflow: "hidden",
                    "& .MuiTypography-root": {
                        color: `${ALERT_UI.text} !important`,
                    },
                    "& .MuiInputBase-root": {
                        backgroundColor: "#FFFFFF !important",
                        color: `${ALERT_UI.text} !important`,
                    },
                    "& .MuiInputLabel-root": {
                        color: `${ALERT_UI.textSecondary} !important`,
                    },
                    "& .MuiInputLabel-root.Mui-focused": {
                        color: `${ALERT_UI.primary} !important`,
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${ALERT_UI.border} !important`,
                    },
                    "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${ALERT_UI.primaryBorder} !important`,
                    },
                    "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: `${ALERT_UI.primary} !important`,
                    },
                    "& .MuiFormHelperText-root": {
                        color: `${ALERT_UI.textSecondary} !important`,
                    },
                    "& .MuiAutocomplete-paper": {
                        backgroundColor: "#FFFFFF !important",
                        color: `${ALERT_UI.text} !important`,
                    },
                    "& .MuiAutocomplete-option": {
                        backgroundColor: "#FFFFFF",
                        color: `${ALERT_UI.text} !important`,
                        "&[aria-selected='true']": {
                            backgroundColor: `${ALERT_UI.primarySoft} !important`,
                        },
                        "&.Mui-focused": {
                            backgroundColor: "#F4F8F5 !important",
                        },
                    },
                    "& .MuiSvgIcon-root": {
                        color: "inherit",
                    },
                },
            }}
        >
            <DialogTitle
                sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    px: { xs: 2, sm: 3 },
                    py: 2.2,
                    backgroundColor: ALERT_UI.surface,
                    borderBottom: `1px solid ${ALERT_UI.border}`,
                }}
            >
                <Box>
                    <Typography
                        variant="h4"
                        component="div"
                        fontWeight={800}
                        sx={{ color: ALERT_UI.text }}
                    >
                        Alerta de retorno
                    </Typography>

                    <Typography
                        variant="body2"
                        sx={{
                            mt: 0.5,
                            color: ALERT_UI.textSecondary,
                        }}
                    >
                        O alerta será disparado quando o produto
                        voltar de qualquer vendedora.
                    </Typography>
                </Box>

                <IconButton
                    onClick={handleClose}
                    disabled={isSaving}
                    aria-label="Fechar"
                    sx={{
                        color: ALERT_UI.textSecondary,
                        backgroundColor: ALERT_UI.surfaceSoft,
                        border: `1px solid ${ALERT_UI.border}`,
                        "&:hover": {
                            backgroundColor: "#EEF1F4",
                        },
                    }}
                >
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent
                dividers
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2.5,
                    backgroundColor: ALERT_UI.background,
                    borderColor: ALERT_UI.border,
                }}
            >
                <Stack spacing={2.2}>
                    <Collapse in={Boolean(feedback.message)}>
                        {feedback.message ? (
                            <Alert
                                severity={feedback.type || "info"}
                                onClose={clearFeedback}
                            >
                                {feedback.message}
                            </Alert>
                        ) : null}
                    </Collapse>

                    <Autocomplete
                        componentsProps={LIGHT_AUTOCOMPLETE_COMPONENTS_PROPS}
                        options={productOptions}
                        value={selectedProduct}
                        inputValue={productSearch}
                        loading={isSearchingProducts}
                        filterOptions={(options) => options}
                        isOptionEqualToValue={(option, value) =>
                            option.content_type_id ===
                                value.content_type_id &&
                            option.object_id === value.object_id
                        }
                        getOptionLabel={getProductOptionLabel}
                        onInputChange={(_, newValue, reason) => {
                            if (reason !== "reset") {
                                setProductSearch(newValue);
                            }
                        }}
                        onChange={(_, newValue) => {
                            setSelectedProduct(newValue);

                            if (newValue) {
                                setProductSearch(
                                    getProductOptionLabel(newValue)
                                );
                            }
                        }}
                        noOptionsText={
                            productSearch.trim().length < 2
                                ? "Digite ao menos 2 caracteres"
                                : "Nenhum produto encontrado"
                        }
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                {...props}
                                key={`${option.content_type_id}-${option.object_id}`}
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 1.5,
                                }}
                            >
                                <Box
                                    component="img"
                                    src={option.image_url}
                                    alt={option.product_code}
                                    onError={(event) => {
                                        event.currentTarget.style.display =
                                            "none";
                                    }}
                                    sx={{
                                        width: 46,
                                        height: 46,
                                        borderRadius: 1.5,
                                        objectFit: "cover",
                                        flexShrink: 0,
                                    }}
                                />

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        fontWeight={800}
                                        noWrap
                                        sx={{
                                            color: `${ALERT_UI.text} !important`,
                                        }}
                                    >
                                        {option.product_code}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        noWrap
                                        sx={{
                                            color: `${ALERT_UI.textSecondary} !important`,
                                        }}
                                    >
                                        {[
                                            option.product_model_label ||
                                                option.product_model,
                                            option.product_size
                                                ? `Tam. ${option.product_size}`
                                                : null,
                                            option.color,
                                            option.supplier,
                                        ]
                                            .filter(Boolean)
                                            .join(" • ")}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Produto"
                                placeholder="Pesquise pelo código"
                                required
                                sx={LIGHT_MODAL_FIELD_SX}
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isSearchingProducts ? (
                                                <CircularProgress
                                                    size={20}
                                                />
                                            ) : null}
                                            {
                                                params.InputProps
                                                    .endAdornment
                                            }
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    {selectedProduct ? (
                        <Card
                            variant="outlined"
                            sx={{
                                borderRadius: 2.5,
                                borderColor: "#B7DEC8",
                                backgroundColor: ALERT_UI.surface,
                                boxShadow: "0 8px 24px rgba(15, 23, 42, 0.05)",
                            }}
                        >
                            <CardContent
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 2,
                                    "&:last-child": {
                                        pb: 2,
                                    },
                                }}
                            >
                                <Box
                                    component="img"
                                    src={selectedProduct.image_url}
                                    alt={selectedProduct.product_code}
                                    onError={(event) => {
                                        event.currentTarget.style.display =
                                            "none";
                                    }}
                                    sx={{
                                        width: 74,
                                        height: 74,
                                        borderRadius: 2,
                                        objectFit: "cover",
                                        flexShrink: 0,
                                    }}
                                />

                                <Box sx={{ minWidth: 0 }}>
                                    <Typography
                                        variant="h5"
                                        fontWeight={800}
                                    >
                                        {
                                            selectedProduct.product_code
                                        }
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                    >
                                        {[
                                            selectedProduct.product_model_label ||
                                                selectedProduct.product_model,
                                            selectedProduct.product_size
                                                ? `Tamanho ${selectedProduct.product_size}`
                                                : null,
                                            selectedProduct.color,
                                            selectedProduct.supplier,
                                        ]
                                            .filter(Boolean)
                                            .join(" • ")}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        spacing={1}
                                        sx={{
                                            mt: 1,
                                            flexWrap: "wrap",
                                            rowGap: 1,
                                        }}
                                    >
                                        <Chip
                                            size="small"
                                            label={`Estoque: ${
                                                selectedProduct.quantity_available ??
                                                0
                                            }`}
                                        />

                                        <Chip
                                            size="small"
                                            label={`Valor: R$ ${Number(
                                                selectedProduct.charged_price ||
                                                    0
                                            ).toLocaleString("pt-BR", {
                                                minimumFractionDigits: 2,
                                                maximumFractionDigits: 2,
                                            })}`}
                                        />
                                    </Stack>
                                </Box>
                            </CardContent>
                        </Card>
                    ) : null}

                    <Autocomplete
                        componentsProps={LIGHT_AUTOCOMPLETE_COMPONENTS_PROPS}
                        options={sellerOptions}
                        value={selectedSeller}
                        loading={isLoadingSellers}
                        isOptionEqualToValue={(option, value) =>
                            option.id === value.id
                        }
                        getOptionLabel={(option) =>
                            option?.nome || option?.email || ""
                        }
                        onChange={(_, newValue) =>
                            setSelectedSeller(newValue)
                        }
                        noOptionsText="Nenhuma vendedora encontrada"
                        loadingText="Carregando vendedoras..."
                        renderOption={(props, option) => (
                            <Box
                                component="li"
                                {...props}
                                key={option.id}
                                sx={{
                                    display: "flex",
                                    flexDirection: "column",
                                    alignItems: "flex-start !important",
                                    gap: 0.25,
                                    color: `${ALERT_UI.text} !important`,
                                }}
                            >
                                <Typography
                                    fontWeight={700}
                                    sx={{
                                        color: `${ALERT_UI.text} !important`,
                                    }}
                                >
                                    {option.nome || "Vendedora sem nome"}
                                </Typography>

                                {option.email ? (
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: `${ALERT_UI.textSecondary} !important`,
                                        }}
                                    >
                                        {option.email}
                                    </Typography>
                                ) : null}
                            </Box>
                        )}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label="Vendedora solicitante"
                                placeholder="Opcional"
                                sx={LIGHT_MODAL_FIELD_SX}
                                helperText="Informa quem pediu o produto. Não interfere em quem fará a devolução."
                                InputProps={{
                                    ...params.InputProps,
                                    endAdornment: (
                                        <>
                                            {isLoadingSellers ? (
                                                <CircularProgress
                                                    size={20}
                                                />
                                            ) : null}
                                            {
                                                params.InputProps
                                                    .endAdornment
                                            }
                                        </>
                                    ),
                                }}
                            />
                        )}
                    />

                    <TextField
                        label="Descrição do alerta"
                        placeholder="Ex.: Cliente aguardando esta peça."
                        sx={LIGHT_MODAL_FIELD_SX}
                        value={message}
                        onChange={(event) =>
                            setMessage(event.target.value)
                        }
                        multiline
                        minRows={3}
                        maxRows={6}
                        required
                        inputProps={{
                            maxLength: 500,
                        }}
                        helperText={`${message.length}/500`}
                    />

                    <Divider sx={{ borderColor: ALERT_UI.border }} />

                    <Box>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={2}
                            sx={{ mb: 1.5 }}
                        >
                            <Box>
                                <Typography
                                    variant="h5"
                                    fontWeight={800}
                                >
                                    Alertas ativos
                                </Typography>

                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                >
                                    {activeAlerts.length} alerta
                                    {activeAlerts.length === 1
                                        ? ""
                                        : "s"}{" "}
                                    aguardando retorno.
                                </Typography>
                            </Box>

                            {isLoadingAlerts ? (
                                <CircularProgress size={22} />
                            ) : null}
                        </Stack>

                        {!isLoadingAlerts &&
                        activeAlerts.length === 0 ? (
                            <Box
                                sx={{
                                    py: 3,
                                    px: 2,
                                    border: "1px dashed",
                                    borderColor: ALERT_UI.border,
                                    backgroundColor: ALERT_UI.surface,
                                    borderRadius: 2.5,
                                    textAlign: "center",
                                }}
                            >
                                <Typography color="text.secondary">
                                    Nenhum alerta ativo.
                                </Typography>
                            </Box>
                        ) : (
                            <Stack spacing={1.2}>
                                {activeAlerts.map((alert) => {
                                    const snapshot =
                                        alert.product_snapshot || {};

                                    return (
                                        <Card
                                            key={alert.id}
                                            variant="outlined"
                                            sx={{
                                                borderRadius: 2.5,
                                                borderColor: ALERT_UI.border,
                                                backgroundColor: ALERT_UI.surface,
                                                boxShadow: "0 4px 16px rgba(15, 23, 42, 0.04)",
                                            }}
                                        >
                                            <CardContent
                                                sx={{
                                                    display: "flex",
                                                    alignItems:
                                                        "flex-start",
                                                    justifyContent:
                                                        "space-between",
                                                    gap: 2,
                                                    "&:last-child": {
                                                        pb: 2,
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        display: "flex",
                                                        alignItems:
                                                            "flex-start",
                                                        gap: 1.5,
                                                        minWidth: 0,
                                                    }}
                                                >
                                                    {snapshot.image_url ? (
                                                        <Box
                                                            component="img"
                                                            src={
                                                                snapshot.image_url
                                                            }
                                                            alt={
                                                                alert.product_code
                                                            }
                                                            onError={(
                                                                event
                                                            ) => {
                                                                event.currentTarget.style.display =
                                                                    "none";
                                                            }}
                                                            sx={{
                                                                width: 56,
                                                                height: 56,
                                                                borderRadius: 1.5,
                                                                objectFit:
                                                                    "cover",
                                                                flexShrink: 0,
                                                            }}
                                                        />
                                                    ) : null}

                                                    <Box
                                                        sx={{
                                                            minWidth: 0,
                                                        }}
                                                    >
                                                        <Stack
                                                            direction="row"
                                                            alignItems="center"
                                                            spacing={1}
                                                            sx={{
                                                                flexWrap:
                                                                    "wrap",
                                                                rowGap: 0.7,
                                                            }}
                                                        >
                                                            <Typography
                                                                fontWeight={
                                                                    800
                                                                }
                                                            >
                                                                {
                                                                    alert.product_code
                                                                }
                                                            </Typography>

                                                            <Chip
                                                                size="small"
                                                                label="Aguardando"
                                                                sx={{
                                                                    backgroundColor: "#FFF7E6",
                                                                    color: "#9A6700",
                                                                    fontWeight: 700,
                                                                }}
                                                            />

                                                            {alert.product_size ? (
                                                                <Chip
                                                                    size="small"
                                                                    variant="outlined"
                                                                    label={`Tam. ${alert.product_size}`}
                                                                />
                                                            ) : null}
                                                        </Stack>

                                                        <Typography
                                                            variant="body2"
                                                            color="text.secondary"
                                                            sx={{
                                                                mt: 0.5,
                                                            }}
                                                        >
                                                            {[
                                                                alert.product_model,
                                                                alert
                                                                    .requested_by_seller
                                                                    ?.nome
                                                                    ? `Solicitado por ${alert.requested_by_seller.nome}`
                                                                    : "Sem vendedora solicitante",
                                                            ]
                                                                .filter(
                                                                    Boolean
                                                                )
                                                                .join(
                                                                    " • "
                                                                )}
                                                        </Typography>

                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                mt: 0.8,
                                                                whiteSpace:
                                                                    "pre-wrap",
                                                            }}
                                                        >
                                                            {
                                                                alert.message
                                                            }
                                                        </Typography>

                                                        <Typography
                                                            variant="caption"
                                                            color="text.secondary"
                                                            sx={{
                                                                display:
                                                                    "block",
                                                                mt: 0.7,
                                                            }}
                                                        >
                                                            Criado em{" "}
                                                            {formatDateTimeBR(
                                                                alert.created_at
                                                            )}
                                                        </Typography>
                                                    </Box>
                                                </Box>

                                                <Tooltip title="Cancelar alerta">
                                                    <span>
                                                        <IconButton
                                                            disabled={
                                                                cancelingAlertId ===
                                                                alert.id
                                                            }
                                                            onClick={() =>
                                                                handleCancelAlert(
                                                                    alert.id
                                                                )
                                                            }
                                                            aria-label="Cancelar alerta"
                                                            sx={{
                                                                color: ALERT_UI.danger,
                                                                backgroundColor: ALERT_UI.dangerSoft,
                                                                border: "1px solid #FECDD3",
                                                                "&:hover": {
                                                                    backgroundColor: "#FFE4E6",
                                                                },
                                                            }}
                                                        >
                                                            {cancelingAlertId ===
                                                            alert.id ? (
                                                                <CircularProgress
                                                                    size={
                                                                        20
                                                                    }
                                                                />
                                                            ) : (
                                                                <DeleteOutlineIcon />
                                                            )}
                                                        </IconButton>
                                                    </span>
                                                </Tooltip>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                            </Stack>
                        )}
                    </Box>
                </Stack>
            </DialogContent>

            <DialogActions
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 2,
                    backgroundColor: ALERT_UI.surface,
                    borderTop: `1px solid ${ALERT_UI.border}`,
                }}
            >
                <Button
                    onClick={handleClose}
                    disabled={isSaving}
                    sx={{
                        textTransform: "none",
                        color: ALERT_UI.textSecondary,
                        fontWeight: 700,
                    }}
                >
                    Fechar
                </Button>

                <Button
                    variant="contained"
                    onClick={handleCreateAlert}
                    disabled={
                        isSaving ||
                        !selectedProduct ||
                        !message.trim()
                    }
                    startIcon={
                        isSaving ? (
                            <CircularProgress
                                size={18}
                                color="inherit"
                            />
                        ) : (
                            <AddAlertOutlinedIcon />
                        )
                    }
                    sx={{
                        textTransform: "none",
                        fontWeight: 800,
                        borderRadius: 2,
                        px: 2.4,
                        background: `${ALERT_UI.primary} !important`,
                        color: "#FFFFFF !important",
                        boxShadow: "none",
                        "&:hover": {
                            background: `${ALERT_UI.primaryDark} !important`,
                            boxShadow: "none",
                        },
                        "&.Mui-disabled": {
                            backgroundColor: "#D8E7DE",
                            color: "#82988B",
                        },
                    }}
                >
                    {isSaving
                        ? "Salvando..."
                        : "Criar alerta"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};


const OpenProdsPage = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const isSmallScreen = useMediaQuery(
        theme.breakpoints.down("md")
    );

    const [isLoading, setIsLoading] = useState(false);
    const [dataToShow, setDataToShow] = useState({});
    const [dataToExcel, setDataToExcel] = useState([]);
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredData, setFilteredData] = useState({});
    const [selected, setSelected] = useState([]);

    const [alertModalOpen, setAlertModalOpen] =
        useState(false);

    const isAdmin = useSelector(selectIsAdminUser);
    const isStaff = useSelector(selectIsStaffUser);
    const isOfficeStaff = useSelector(
        selectIsOfficeStaffUser
    );

    const canShowPage =
        isAdmin && isStaff && isOfficeStaff;

    useEffect(() => {
        let componentIsMounted = true;

        const fetchData = async () => {
            setIsLoading(true);

            try {
                const headers = getAuthHeaders();

                const [openProductsResponse, stockResponse] =
                    await Promise.all([
                        djangoApi.get(
                            ENDPOINTS.openProducts,
                            {
                                headers,
                            }
                        ),
                        djangoApi.get(
                            ENDPOINTS.stockProducts,
                            {
                                headers,
                            }
                        ),
                    ]);

                const sellerProducts =
                    openProductsResponse.data?.dados?.data || [];

                const stockGroupedData =
                    stockResponse.data?.dados || {};

                const stockProducts = Object.entries(
                    stockGroupedData
                ).flatMap(([model, items]) =>
                    (items || []).map((item) => ({
                        sell_price: item?.charged_price,
                        kit__kit_number: 0,
                        kit__pay_date: "",
                        kit__seller__nome: "Pitaya Joias",
                        content_type__model: model,
                        quantity: item?.quantity_available,
                        pk: item?.pk,
                        product_id_produto:
                            item?.id_produto,
                        product_tamanho:
                            item?.tamanho || 0,
                    }))
                );

                const combined = [
                    ...sellerProducts,
                    ...stockProducts,
                ];

                const groupedData = [...combined]
                    .sort(
                        (a, b) =>
                            Number(
                                b.product_id_produto || 0
                            ) -
                            Number(
                                a.product_id_produto || 0
                            )
                    )
                    .reduce((accumulator, item) => {
                        const key =
                            item.content_type__model ||
                            "outros";

                        if (!accumulator[key]) {
                            accumulator[key] = [];
                        }

                        accumulator[key].push(item);

                        return accumulator;
                    }, {});

                if (componentIsMounted) {
                    setDataToExcel(combined);
                    setDataToShow(groupedData);
                    setFilteredData(groupedData);
                }
            } catch (error) {
                console.error(
                    "Erro ao buscar dados:",
                    error
                );
            } finally {
                if (componentIsMounted) {
                    setIsLoading(false);
                }
            }
        };

        fetchData();

        return () => {
            componentIsMounted = false;
        };
    }, []);

    useEffect(() => {
        let temporaryFiltered = {
            ...dataToShow,
        };

        if (selected.length) {
            temporaryFiltered = Object.keys(
                temporaryFiltered
            ).reduce((accumulator, key) => {
                const filteredItems =
                    temporaryFiltered[key].filter(
                        (item) => {
                            if (
                                selected.includes(
                                    "Vendedoras"
                                ) &&
                                selected.includes(
                                    "Estoque"
                                )
                            ) {
                                return true;
                            }

                            if (
                                selected.includes(
                                    "Vendedoras"
                                )
                            ) {
                                return (
                                    item.kit__seller__nome !==
                                    "Pitaya Joias"
                                );
                            }

                            if (
                                selected.includes("Estoque")
                            ) {
                                return (
                                    item.kit__seller__nome ===
                                    "Pitaya Joias"
                                );
                            }

                            return true;
                        }
                    );

                if (filteredItems.length > 0) {
                    accumulator[key] = filteredItems;
                }

                return accumulator;
            }, {});
        }

        if (searchQuery.trim()) {
            const normalizedQuery = searchQuery
                .trim()
                .toLowerCase();

            temporaryFiltered = Object.keys(
                temporaryFiltered
            ).reduce((accumulator, key) => {
                const filteredItems =
                    temporaryFiltered[key].filter(
                        (item) =>
                            JSON.stringify(item)
                                .toLowerCase()
                                .includes(normalizedQuery)
                    );

                if (filteredItems.length > 0) {
                    accumulator[key] = filteredItems;
                }

                return accumulator;
            }, {});
        }

        setFilteredData(temporaryFiltered);
    }, [searchQuery, dataToShow, selected]);

    const activeFilterLabel = useMemo(
        () => selected.join(", "),
        [selected]
    );

    const handleExcelFile = () => {
        exportToExcelOpenProds(dataToExcel);
    };

    const handleFilterChange = (event) => {
        const value = event.target.value;

        setSelected(
            typeof value === "string"
                ? value.split(",")
                : value
        );
    };

    if (isLoading) {
        return (
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    minHeight: "70vh",
                }}
            >
                <CircularProgress />
            </Box>
        );
    }

    return (
        <>
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "flex-start",
                    alignItems: "center",
                    width: "100%",
                    maxWidth: 1200,
                    minHeight: "100%",
                    pt: {
                        xs: 3,
                        md: 7.5,
                    },
                    px: {
                        xs: 2,
                        md: 3,
                    },
                    mx: "auto",
                    gap: 2.5,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        flexDirection: {
                            xs: "column",
                            md: "row",
                        },
                        justifyContent: "space-between",
                        alignItems: {
                            xs: "stretch",
                            md: "center",
                        },
                        gap: 2,
                    }}
                >
                    <Stack
                        direction={{
                            xs: "column",
                            sm: "row",
                        }}
                        spacing={1.2}
                        sx={{
                            width: {
                                xs: "100%",
                                md: "auto",
                            },
                        }}
                    >
                        <TextField
                            variant="outlined"
                            placeholder="Insira um ID"
                            value={searchQuery}
                            onChange={(event) =>
                                setSearchQuery(
                                    event.target.value
                                )
                            }
                            inputProps={{
                                maxLength: 20,
                            }}
                            sx={{
                                width: {
                                    xs: "100%",
                                    sm: 220,
                                },
                            }}
                        />

                        <Button
                            variant="contained"
                            size="small"
                            startIcon={
                                <AddAlertOutlinedIcon sx={{ fontSize: 16 }} />
                            }
                            onClick={() =>
                                setAlertModalOpen(true)
                            }
                            sx={{
                                alignSelf: { xs: "flex-start", sm: "center" },
                                height: 34,
                                minWidth: 0,
                                px: 1.4,
                                whiteSpace: "nowrap",
                                textTransform: "none",
                                fontSize: 13,
                                lineHeight: 1,
                                fontWeight: 700,
                                borderRadius: "10px",
                                color: "#FFFFFF !important",
                                background: `${ALERT_UI.primary} !important`,
                                boxShadow: "0 3px 10px rgba(62, 124, 89, 0.18)",
                                "& .MuiButton-startIcon": {
                                    mr: 0.65,
                                },
                                "&:hover": {
                                    color: "#FFFFFF !important",
                                    background: `${ALERT_UI.primaryDark} !important`,
                                    boxShadow: "0 5px 14px rgba(62, 124, 89, 0.24)",
                                },
                            }}
                        >
                            Alerta
                        </Button>
                    </Stack>

                    <Stack
                        direction="row"
                        spacing={1}
                        alignItems="center"
                        sx={{
                            width: {
                                xs: "100%",
                                md: "auto",
                            },
                        }}
                    >
                        <FormControl
                            size="small"
                            sx={{
                                width: {
                                    xs: "100%",
                                    sm: 250,
                                },
                                minWidth: 200,
                                "& .MuiOutlinedInput-root": {
                                    "&:hover .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor:
                                                "white",
                                        },
                                    "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                        {
                                            borderColor:
                                                "white",
                                        },
                                },
                                "& .MuiInputLabel-root": {
                                    color: "white",
                                    "&.Mui-focused": {
                                        color: "white",
                                    },
                                },
                            }}
                        >
                            <InputLabel>
                                Filtrar por
                            </InputLabel>

                            <Select
                                multiple
                                value={selected}
                                onChange={
                                    handleFilterChange
                                }
                                input={
                                    <OutlinedInput label="Filtrar por" />
                                }
                                renderValue={() =>
                                    activeFilterLabel
                                }
                            >
                                {FILTER_OPTIONS.map(
                                    (option) => (
                                        <MenuItem
                                            key={option}
                                            value={option}
                                        >
                                            <Checkbox
                                                checked={selected.includes(
                                                    option
                                                )}
                                                color="success"
                                            />

                                            <ListItemText
                                                primary={
                                                    option
                                                }
                                            />
                                        </MenuItem>
                                    )
                                )}
                            </Select>
                        </FormControl>

                        <Tooltip title="Exportar para Excel">
                            <IconButton
                                onClick={handleExcelFile}
                                sx={{
                                    width: 48,
                                    height: 48,
                                    flexShrink: 0,
                                }}
                            >
                                <FontAwesomeIcon
                                    icon={faFileExcel}
                                    size="lg"
                                    color={
                                        colors.greenAccent[600]
                                    }
                                />
                            </IconButton>
                        </Tooltip>
                    </Stack>
                </Box>

                <Box
                    sx={{
                        width: "100%",
                        display: "flex",
                        justifyContent: "center",
                    }}
                >
                    {Object.keys(filteredData).length >
                    0 ? (
                        <ProductAccordion
                            groupedData={filteredData}
                            canShowPage={canShowPage}
                        />
                    ) : (
                        <Typography>
                            Nenhum produto encontrado para
                            este filtro.
                        </Typography>
                    )}
                </Box>
            </Box>

            <ProductReturnAlertModal
                open={alertModalOpen}
                onClose={() =>
                    setAlertModalOpen(false)
                }
                colors={colors}
            />
        </>
    );
};


export default OpenProdsPage;
